import { useMutation } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { useButtonMenu, useConfirmationDialog, useCustomSnackbar } from '@procureai/ui-mui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TsrctContractDemandStatus } from '@/generated/gql/graphql.ts';
import { RouteId } from '~/routes';
import { ContractDemandCancelDialog } from '~/routes/autonomousSourcing/ContractDemandDetail/components/ContractDemandCancelDialog.tsx';
import { DELETE_CONTRACT_DEMAND_MUTATION } from '~/routes/autonomousSourcing/ContractDemandDetail/ContractDemandDetail.gql.ts';

type ContractDemandOverFlowMenuProps = {
  id: string;
  status?: string;
};

export const ContractDemandOverFlowMenu = ({ id, status }: ContractDemandOverFlowMenuProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'ContractDemandDetail' });
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { enqueueSuccessSnackbar, onError } = useCustomSnackbar();
  const { confirm } = useConfirmationDialog();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const [deleteDemand, { loading: deleteLoading }] = useMutation(DELETE_CONTRACT_DEMAND_MUTATION, { onError });
  const { menuProps, buttonProps } = useButtonMenu();

  const handleDelete = async () => {
    const { errors } = await deleteDemand({ variables: { id } });
    if (!errors) {
      enqueueSuccessSnackbar(t('action.delete.success'));
      navigate(RouteId.contractDemand);
    }
  };

  return (
    <div>
      <ContractDemandCancelDialog demandId={id} open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} />
      <IconButton
        {...buttonProps}
        disabled={!hasPermission({ any: [Permission.ContractDemandDelete, Permission.ContractDemandCancel] })}
        loading={deleteLoading}
      >
        <MoreVert />
      </IconButton>
      <Menu {...menuProps}>
        <MenuItem
          disabled={!hasPermission(Permission.ContractDemandCancel) || status === TsrctContractDemandStatus.CANCELLED}
          onClick={() => setCancelDialogOpen(true)}
        >
          {t('action.cancel.label')}
        </MenuItem>
        <MenuItem
          disabled={!hasPermission(Permission.ContractDemandDelete)}
          onClick={confirm(handleDelete, { message: t('action.delete.confirm') })}
        >
          {t('action.delete.label')}
        </MenuItem>
      </Menu>
    </div>
  );
};
